import type { AtendenteDTO, StatusAtendente } from '@viniko/shared';
import { useQueryClient } from '@tanstack/react-query';
import {
  Bell,
  GraduationCap,
  LogOut,
  MessageCircle,
  Moon,
  Package,
  Settings,
  Sun,
  Users,
  WifiOff,
} from 'lucide-react';
import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { LogoCotacao } from '../componentes/LogoCotacao';
import { LogoWhatsapp } from '../componentes/LogoWhatsapp';
import { iniciais, useToast } from '../componentes/ui';
import { patch, post } from '../lib/api';
import { pedirPermissao, permissaoNotificacao } from '../lib/avisos';
import { chaves, useContagem } from '../lib/consultas';
import { alternarTema, useCorAtendente, useTemaEscuro } from '../lib/tema';
import { useTempoReal } from '../lib/tempoReal';
import './shell.css';

const STATUS_ROTULO: Record<StatusAtendente, string> = {
  online: 'Online',
  ausente: 'Ausente',
  offline: 'Offline',
};

export function Shell({ eu }: { eu: AtendenteDTO }) {
  const conexao = useTempoReal(eu);
  const contagem = useContagem();
  const escuro = useTemaEscuro();
  const cor = useCorAtendente(eu.cor);
  const qc = useQueryClient();
  const navegar = useNavigate();
  const toast = useToast();
  const [menu, setMenu] = useState(false);
  const [permissao, setPermissao] = useState(permissaoNotificacao());

  const pendentes = (contagem.data?.minhasResponder ?? 0) + (contagem.data?.fila ?? 0);

  async function mudarStatus(status: StatusAtendente) {
    await patch('/api/eu/status', { status });
    qc.setQueryData<AtendenteDTO>(chaves.eu, (a) => (a ? { ...a, status } : a));
    setMenu(false);
  }

  async function sair() {
    await post('/api/auth/logout');
    qc.clear();
    navegar('/entrar', { replace: true });
  }

  const itens = [
    { para: '/conversas', rotulo: 'Conversas', icone: <MessageCircle />, badge: pendentes },
    { para: '/contatos', rotulo: 'Contatos', icone: <Users /> },
    { para: '/cotacao', rotulo: 'Cotação', icone: <LogoCotacao /> },
    { para: '/pedidos', rotulo: 'Pedidos', icone: <Package /> },
    { para: '/treino', rotulo: 'Treino', icone: <GraduationCap /> },
    { para: '/configuracoes', rotulo: 'Configurações', icone: <Settings /> },
  ];

  return (
    <div className="shell">
      <nav className="rail" aria-label="Menu principal">
        <div className="logo" title="ChatFlow">
          <LogoWhatsapp />
        </div>
        {itens.map((i) => (
          <NavLink
            key={i.para}
            to={i.para}
            className="rail-btn"
            title={i.rotulo}
            aria-label={i.rotulo}
          >
            {i.icone}
            {!!i.badge && <span className="rail-badge">{i.badge > 99 ? '99+' : i.badge}</span>}
          </NavLink>
        ))}

        <div className="rail-fim">
          {permissao === 'default' && (
            <button
              className="rail-btn alerta"
              title="Ativar notificações do navegador"
              aria-label="Ativar notificações do navegador"
              onClick={async () => {
                await pedirPermissao();
                setPermissao(permissaoNotificacao());
              }}
            >
              <Bell />
            </button>
          )}
          {conexao === 'desconectado' && (
            <span className="rail-btn erro" title="Sem conexão em tempo real. Tentando reconectar…">
              <WifiOff />
            </span>
          )}
          <button
            className="rail-btn"
            onClick={alternarTema}
            title={escuro ? 'Tema claro' : 'Tema escuro'}
          >
            {escuro ? <Sun /> : <Moon />}
          </button>
          <div className="eu-wrap">
            <button
              className={`eu st-${eu.status}`}
              style={{ background: cor.fundo, color: cor.texto }}
              onClick={() => setMenu((m) => !m)}
              aria-expanded={menu}
              aria-label={`${eu.nomeExibicao}, ${STATUS_ROTULO[eu.status]}. Abrir menu da conta`}
            >
              {iniciais(eu.nomeExibicao)}
            </button>
            {menu && (
              <div className="eu-menu entra" role="menu">
                <div className="eu-menu-topo">
                  <b>{eu.nome}</b>
                  <span>{eu.email}</span>
                </div>
                {(['online', 'ausente'] as const).map((s) => (
                  <button
                    key={s}
                    role="menuitemradio"
                    aria-checked={eu.status === s}
                    onClick={() => mudarStatus(s)}
                  >
                    <span className={`bolinha st-${s}`} /> {STATUS_ROTULO[s]}
                    {s === 'ausente' && <small>a equipe vê que você saiu</small>}
                  </button>
                ))}
                <hr />
                <button
                  role="menuitem"
                  onClick={() => {
                    setMenu(false);
                    navegar('/configuracoes');
                  }}
                >
                  <Settings /> Meu perfil
                </button>
                <button
                  role="menuitem"
                  onClick={() => sair().catch((e) => toast((e as Error).message, 'erro'))}
                >
                  <LogOut /> Sair
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>
      <div className="conteudo">
        <Outlet />
      </div>
    </div>
  );
}
